import React from "react";
import { Link } from "react-router-dom";
import "./styles/About.css";

const platformCards = [
  {
    title: "What we do",
    text: "We help organizations plan, secure, and improve modern IT environments across networking, cloud, cybersecurity, and infrastructure."
  },
  {
    title: "How we work",
    text: "Our approach combines consulting, implementation, and continuous improvement so every engagement leads to practical operational value."
  },
  {
    title: "Why clients choose us",
    text: "Clients rely on Com Networks for clear guidance, technical depth, and solutions built around reliability, security, and long-term performance."
  }
];

const valuePoints = [
  {
    title: "Mission",
    text: "To help organizations strengthen their technology environments with secure, scalable, and well-planned solutions that support both daily operations and future growth."
  },
  {
    title: "Vision",
    text: "To be the trusted IT partner businesses turn to when they need clarity, resilience, and confidence in their infrastructure and security decisions."
  }
];

const About = () => {
  return (
    <div className="aboutPage">
      <section className="aboutHero">
        <div className="aboutHero__container">
          <div className="aboutHero__content">
            <p className="aboutHero__kicker">About Com Networks</p>

            <h1 className="aboutHero__title">
              Secure infrastructure. Smarter operations. Stronger IT foundations.
            </h1>

            <p className="aboutHero__lead">
              Com Networks helps organizations build, secure, and scale modern IT
              environments through expert consulting across networking, cloud,
              cybersecurity, and infrastructure. We focus on practical solutions
              that improve resilience, performance, and long-term operational
              confidence.
            </p>

            <div className="aboutHero__ctaRow">
              <Link to="/contact" className="aboutBtn aboutBtn--primary">
                Talk to Us
              </Link>
              <Link to="/services" className="aboutBtn aboutBtn--ghost">
                Explore Services
              </Link>
            </div>

            <div className="aboutHero__miniStats">
              <div className="aboutStat">
                <div className="aboutStat__num">Networking & Security</div>
                <div className="aboutStat__text">
                  Built for reliability, visibility, and protection.
                </div>
              </div>

              <div className="aboutStat">
                <div className="aboutStat__num">Cloud & Infrastructure</div>
                <div className="aboutStat__text">
                  Scalable environments designed for modern operations.
                </div>
              </div>

              <div className="aboutStat">
                <div className="aboutStat__num">Strategy & Delivery</div>
                <div className="aboutStat__text">
                  Practical guidance from planning through implementation.
                </div>
              </div>
            </div>
          </div>

          <div className="aboutHero__visual">
            <span className="aboutHero__accent aboutHero__accent--a" />
            <span className="aboutHero__accent aboutHero__accent--b" />

            <div className="aboutHero__card">
              <h3 className="aboutHero__cardTitle">Built for critical environments</h3>
              <p className="aboutHero__cardText">
                We support organizations that need dependable systems, stronger
                security, and technology decisions that stand up to real
                operational pressure.
              </p>
            </div>

            <div className="aboutHero__badge">Trusted technology guidance</div>
          </div>
        </div>
      </section>

      <section className="aboutSection aboutSection--white">
        <div className="aboutContainer">
          <h2 className="aboutH2">Who we are</h2>

          <p className="aboutP">
            Com Networks is an IT consulting and cybersecurity company that works
            with organizations looking for secure, scalable, and dependable
            technology foundations. Our work spans networking, cloud,
            infrastructure, and security, with a focus on making complex
            environments easier to manage and safer to operate.
          </p>

          <p className="aboutP">
            We act as a practical technology partner, helping teams move from
            uncertainty to clarity with solutions that are aligned to business
            needs, risk priorities, and growth goals.
          </p>

          <div className="aboutGrid3">
            {platformCards.map((card) => (
              <article key={card.title} className="aboutCard">
                <h3 className="aboutCard__title">{card.title}</h3>
                <p className="aboutCard__text">{card.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="aboutSection aboutSection--muted">
        <div className="aboutContainer">
          <div className="aboutSplit">
            <div>
              <h2 className="aboutH2">What drives our work</h2>

              <p className="aboutP">
                Our approach starts with understanding the environment, the risks,
                and the business priorities behind every technical decision. From
                there, we help shape a roadmap that is practical, secure, and built
                for real-world performance.
              </p>

              <p className="aboutP">
                Whether the need is cloud adoption, infrastructure modernization,
                stronger cybersecurity, or more reliable network performance, we
                focus on solutions that are clear, sustainable, and aligned with
                operational goals.
              </p>

              <ul className="aboutList">
                <li>Assess infrastructure, risk, and performance gaps.</li>
                <li>Recommend practical improvements based on business priorities.</li>
                <li>Support deployment, optimization, and long-term improvement.</li>
                <li>Strengthen security posture without unnecessary complexity.</li>
              </ul>

              <div className="aboutNote">
                <span className="aboutNote__dot" />
                <p className="aboutNote__text">
                  Engagements can begin with planning and assessment, then expand
                  into implementation, optimization, and ongoing strategic support.
                </p>
              </div>
            </div>

            <div>
              <div className="aboutGrid2">
                {valuePoints.map((point) => (
                  <article key={point.title} className="aboutCard aboutCard--tight">
                    <h3 className="aboutCard__title">Our {point.title.toLowerCase()}</h3>
                    <p className="aboutCard__text">{point.text}</p>
                  </article>
                ))}
              </div>

              <div className="aboutHighlight">
                <span className="aboutHighlight__bar" />
                <p className="aboutHighlight__text">
                  We believe technology should reduce complexity, strengthen
                  operations, and give teams the confidence to move faster.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="aboutCta">
        <div className="aboutCta__container">
          <div>
            <h2 className="aboutCta__title">
              Let’s build a more secure and reliable IT environment
            </h2>

            <p className="aboutCta__text">
              If your organization is planning infrastructure improvements,
              evaluating cloud strategy, or strengthening cybersecurity, we can
              help you take the next step with practical guidance and experienced
              support.
            </p>

            <div className="aboutCta__buttons">
              <Link to="/contact" className="aboutBtn aboutBtn--primary">
                Start a Conversation
              </Link>
              <Link to="/services" className="aboutBtn aboutBtn--ghostOnDark">
                View Solutions
              </Link>
            </div>
          </div>

          <div className="aboutCta__panel">
            <h3 className="aboutCta__panelTitle">Where we can help</h3>
            <ul className="aboutCta__panelList">
              <li>Networking and infrastructure planning</li>
              <li>Cloud and hybrid environment strategy</li>
              <li>Cybersecurity improvement initiatives</li>
              <li>Scalable IT solutions for growing teams</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
